import newsAutoData from './newsAuto.json';
import { newsArchive2021 } from './newsArchive2021';
import { newsArchive2022 } from './newsArchive2022';
import { newsArchive2023 } from './newsArchive2023';
import { newsArchive2024 } from './newsArchive2024';
import { newsArchive2025 } from './newsArchive2025';

export const newsHero = {
  title: 'お知らせ',
  titleEn: 'NEWS',
  lead: '日本執事アカデミーからの最新のお知らせを掲載しています。開校日程や説明会、更新情報を随時お届けします。',
};

export const newsCategories = [
  { value: 'all', label: 'すべて' },
  { value: 'schedule', label: '開校情報' },
  { value: 'seminar', label: '説明会' },
  { value: 'update', label: '更新情報' },
  { value: 'recruitment', label: '募集情報' },
  { value: 'other', label: 'その他' },
];

/** 記事内に配置する画像(既存の生成アーカイブの実物のみ)。 */
export interface NewsFigure {
  src: string;
  alt: string;
  caption?: string;
}

/** AEO用の記事内Q&A。 */
export interface NewsQA {
  question: string;
  answer: string;
}

/** サイドバーの開校概要表の1行。 */
export interface NewsOverviewRow {
  label: string;
  value: string;
}

/** 記事末尾の関連リンク(実在ルートのみ)。 */
export interface NewsRelatedLink {
  title: string;
  desc?: string;
  href: string;
}

export interface NewsArticle {
  slug: string;
  date: string;
  category: string;
  categoryLabel: string;
  title: string;
  body: string;
  // ↓ 開校系(作り込み)記事のみ設定する任意フィールド。短報は未設定。
  heroImage?: string;
  heroAlt?: string;
  figures?: NewsFigure[];
  overview?: { heading: string; rows: NewsOverviewRow[] };
  faq?: NewsQA[];
  related?: NewsRelatedLink[];
  keywords?: string[];
  metaDescription?: string;
}

const manualArticles: NewsArticle[] = [
  {
    slug: 'website-open',
    date: '2026年6月13日',
    category: 'update',
    categoryLabel: '更新情報',
    title: '公式サイトをリニューアルしました',
    body: `<p>日本執事アカデミーの公式サイトを本日リニューアルいたしました。</p>
<p>今回のリニューアルでは、アカデミーの概要、コース内容、カリキュラム、受講料金、入学案内など、受講をご検討いただくにあたって必要な情報をより見やすく整理し、ご覧いただきやすい構成へと刷新いたしました。</p>
<p>開校日程や説明会の開催情報につきましては、決定次第こちらのお知らせ欄にてご案内してまいります。</p>
<p>また、資料請求（無料）も承っております。コース内容や料金をまとめた資料をPDFでお届けいたしますので、お気軽にご請求ください。</p>
<p>今後とも日本執事アカデミーをよろしくお願い申し上げます。</p>`,
  },
  {
    slug: 'essential-1-recruitment-201910',
    date: '2019年10月11日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: '日本執事アカデミー開校および第1期エッセンシャルコース受講生募集開始のお知らせ',
    metaDescription:
      '日本執事アカデミー第1期エッセンシャルコースの受講生募集を開始。2020年1月11日開校、全8日間・計48時間を土日中心に。定員6名の完全少人数制で、執事の基礎の所作と接遇を学べる入門課程です。日本の執事学校としての学びの詳細をご案内します。',
    keywords: ['執事学校', '日本の執事学校', 'エッセンシャルコース', '執事 養成', '執事 講座'],
    heroImage: '/images/generated/essential-hero-v2-2026.jpg',
    heroAlt: '日本執事アカデミーの講義室で基礎の所作を学ぶエッセンシャルコースの受講生たち',
    figures: [
      {
        src: '/images/generated/essential-manners-2026.jpg',
        alt: 'テーブルマナーの型を実技で確認する受講生の手元のクローズアップ',
        caption: '基礎の所作は実技を通じて身体に落とし込みます',
      },
    ],
    overview: {
      heading: '第1期エッセンシャルコース 開校概要',
      rows: [
        { label: '開校日', value: '2020年1月11日' },
        { label: '修了日', value: '2020年2月2日（予定）' },
        { label: '定員', value: '6名' },
        { label: '最少催行人数', value: '3名' },
        { label: '授業形態', value: '全8日間・計48時間／土日中心（1日6時間）' },
      ],
    },
    faq: [
      {
        question: '未経験でも受講できますか？',
        answer:
          'はい。エッセンシャルコースは執事の基礎を一から学ぶ入門課程です。18歳以上であれば、経験の有無を問わずご受講いただけます。',
      },
      {
        question: '働きながら通えますか？',
        answer:
          'はい。全8日間の講義はすべて土日に開校いたします。1日6時間の集中講義のため、平日はお仕事をされている方も学んでいただけます。',
      },
      {
        question: '定員に達した場合、途中から参加できますか？',
        answer:
          '定員6名の完全少人数制のため、開校後の途中入学は承っておりません。次期の開校をお待ちいただくか、資料請求にて最新の日程をご確認ください。',
      },
    ],
    related: [
      { title: 'エッセンシャルコース', desc: '基礎の所作と接遇を学ぶ入門課程', href: '/courses/essential' },
      { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
      { title: '開校日程', desc: '各コースの最新スケジュール', href: '/schedule' },
      { title: '入学案内', desc: 'お申込みの流れと条件', href: '/admission' },
    ],
    body: `<p>この度、日本執事アカデミーは2020年1月の開校を予定しており、これに先立ちまして第1期エッセンシャルコースの受講生募集を開始いたします。執事という職業に関心をお持ちの方、接遇やおもてなしの技能を基礎から体系的に身につけたい方に向けた、当アカデミー最初の入門課程です。</p>
<h2>日本の執事学校として目指すもの</h2>
<p>欧米では執事（バトラー）を養成する専門の教育機関が古くから存在しますが、日本において執事の技能と品格を体系立てて学べる場は、これまでごく限られてまいりました。当アカデミーは、日本の執事学校として、所作・接遇・テーブルサービスといった実践的な技能を、少人数制のもとで段階的に習得していただける環境を整えております。単なる知識の伝達にとどまらず、実技を通じて身体に型を落とし込むことを重視した指導方針です。</p>
<h2>第1期エッセンシャルコースについて</h2>
<p>エッセンシャルコースは、執事に求められる基礎の所作と接遇の型を集中的に学ぶ入門課程です。全8日間・計48時間のカリキュラムを、すべて土日に開校いたします。1日6時間（90分×4コマ）の集中講義で、平日はお仕事をされている方も、働きながら無理なく学んでいただけます。開校は2020年1月11日を予定しており、修了は2020年2月2日を見込んでおります。カリキュラムは、立ち居振る舞いの基本から、来客応対、食卓での給仕作法までを、順を追って学べる構成としております。</p>
<h2>募集要項とお申込み</h2>
<p>本コースは完全少人数制とし、定員は6名、最少催行人数は3名といたします。お一人おひとりの習得状況に合わせた丁寧な指導を行うため、定員に達し次第、受付を締め切らせていただきます。お申込みの締切は2019年12月28日です。カリキュラムや受講のお手続きの詳細につきましては、資料請求（無料）にてご案内いたします。執事の学びに初めて触れる方も安心してお取り組みいただけるよう、個別のご相談も承っております。皆さまのご応募を心よりお待ち申し上げております。</p>`,
  },
  {
    slug: 'academy-opening-report-202001',
    date: '2020年1月11日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: '日本執事アカデミー開校のご報告',
    metaDescription:
      '2020年1月11日、日本執事アカデミーが東京・お台場に開校いたしました。同日、第1期エッセンシャルコースを開校。定員6名の完全少人数制で、基礎の所作から接遇までを段階的に学べる日本の執事学校としての歩みをご報告します。',
    keywords: ['執事学校', '日本の執事学校', '開校', '執事 養成', 'お台場'],
    heroImage: '/images/generated/about-hero-2026.jpg',
    heroAlt: 'お台場に開校した日本執事アカデミーの落ち着いた佇まいの応接室',
    figures: [
      {
        src: '/images/generated/essential-napkin-2026.jpg',
        alt: 'ナプキンの折り方を実習する受講生の手元のクローズアップ',
        caption: '開講初日から実技を交えて学びが始まりました',
      },
    ],
    overview: {
      heading: 'エッセンシャルコース 開校概要',
      rows: [
        { label: '開校日', value: '2020年1月11日' },
        { label: '修了日', value: '2020年2月2日（予定）' },
        { label: '定員', value: '6名' },
        { label: '最少催行人数', value: '3名' },
        { label: '授業形態', value: '全8日間・計48時間／土日中心（1日6時間）' },
      ],
    },
    faq: [
      {
        question: '講義の見学はできますか？',
        answer:
          '講義中の見学は受講生の集中に配慮し承っておりませんが、資料請求にてカリキュラムや設備の詳細をご案内しております。',
      },
      {
        question: '第2期はいつ開校しますか？',
        answer:
          '第2期以降の日程は、決定次第このお知らせ欄でご案内いたします。資料請求いただいた方には、最新の開校予定を優先的にお届けします。',
      },
    ],
    related: [
      { title: 'アカデミー概要', desc: '日本執事アカデミーについて', href: '/about' },
      { title: 'コース一覧', desc: '3コースの違いを一覧で確認', href: '/courses' },
      { title: '施設案内', desc: '学びの舞台となる校舎と設備', href: '/facilities' },
      { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
    ],
    body: `<p>本日2020年1月11日、日本執事アカデミーは東京・お台場にて開校の運びとなりました。開校にあたりご支援を賜りました皆さまに、心より御礼申し上げます。ここに、これまでの準備の歩みと、当アカデミーが目指す学びの姿をご報告いたします。</p>
<h2>開校にあたって</h2>
<p>当アカデミーは、執事に求められる技能と品格を体系的に学べる場として設立いたしました。日本の執事学校として、基礎の所作から接遇、テーブルサービスまでを、少人数制のもとで段階的に習得していただけるカリキュラムを整えております。お台場という落ち着いた環境のもと、実際の給仕や接遇の場面を想定した実技を重ねられるよう、専用の講義室と実習設備を用意いたしました。</p>
<h2>第1期エッセンシャルコースが開校</h2>
<p>開校と同日、第1期エッセンシャルコースを開校いたしました。全8日間・計48時間の課程を、土日を中心に進めてまいります。1日6時間（90分×4コマ）の集中講義で、働きながら学ばれる受講生にも配慮した日程です。定員は6名、最少催行人数は3名の完全少人数制とし、修了は2020年2月2日を予定しております。初日の講義では、受講生の皆さまが緊張のなかにも真剣な面持ちで、執事としての第一歩を踏み出されました。</p>
<h2>これからの日本執事アカデミー</h2>
<p>修了実技試験を経て、所定の基準を満たされた方には修了証をお渡しいたします。当アカデミーは、単に技能を教える場にとどまらず、執事という職業の誇りと責任を受講生とともに育んでまいります。第2期以降の日程やコースの拡充につきましては、決定次第このお知らせ欄にてご案内いたします。執事の学びにご関心をお持ちの方は、資料請求（無料）よりお気軽にお問い合わせください。今後とも日本執事アカデミーをよろしくお願い申し上げます。</p>`,
  },
  {
    slug: 'covid-19-response-202002',
    date: '2020年2月28日',
    category: 'other',
    categoryLabel: 'その他',
    title: '新型コロナウイルス感染症への当アカデミーの対応について',
    metaDescription:
      '新型コロナウイルス感染症を踏まえた日本執事アカデミーの対応方針をご案内します。完全少人数制（定員6名）の利点を活かし、換気・消毒・検温を徹底。2020年2月8日開校のマスターコース第1期も感染対策を継続のうえ進行します。',
    keywords: ['執事学校', '日本の執事学校', '感染対策', 'マスターコース', '少人数制'],
    heroImage: '/images/generated/facilities-hero-2026.jpg',
    heroAlt: '換気と清潔を保った日本執事アカデミーの講義室',
    figures: [
      {
        src: '/images/generated/about-classroom-2026.jpg',
        alt: '完全少人数制のもと、間隔を保って学ぶ受講環境',
        caption: '少人数制の利点を活かし感染対策を徹底しています',
      },
    ],
    overview: {
      heading: 'マスターコース第1期 開校状況',
      rows: [
        { label: '対象コース', value: 'マスターコース第1期' },
        { label: '開校日', value: '2020年2月8日' },
        { label: '定員', value: '6名' },
        { label: '感染対策', value: '換気・設備消毒・入室時検温' },
        { label: '受講形態', value: '完全少人数制／土日中心（全16日間・計96時間）' },
      ],
    },
    faq: [
      {
        question: '感染対策はどのようにしていますか？',
        answer:
          '完全少人数制（定員6名）の利点を活かし、講義室の換気、設備・備品の消毒、入室時の検温を徹底しております。',
      },
      {
        question: '体調が悪い場合はどうすればよいですか？',
        answer:
          '発熱や体調のすぐれない場合は、無理をなさらずご欠席ください。後日の補講にて対応いたしますのでご安心ください。',
      },
    ],
    related: [
      { title: 'マスターコース', desc: '最上級の総合課程', href: '/courses/master' },
      { title: 'よくある質問', desc: '受講に関するご質問', href: '/faq' },
      { title: 'アクセス', desc: '校舎までの道のり', href: '/access' },
      { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
    ],
    body: `<p>新型コロナウイルス感染症の状況を踏まえ、当アカデミーでは受講生ならびに講師の健康と安全を最優先に、以下の対応を実施してまいります。受講をご検討中の皆さま、ならびに在籍中の受講生の皆さまにおかれましては、ご理解とご協力を賜りますようお願い申し上げます。</p>
<h2>当アカデミーの対応方針</h2>
<p>当アカデミーは完全少人数制（定員6名）の利点を活かし、講義室の十分な換気、設備・備品の消毒、入室時の検温を徹底いたします。大人数が一堂に会する形式をとらないことは、感染リスクを抑えるうえでも有利にはたらきます。日本の執事学校として、学びの質を保ちながら、安全に配慮した運営を両立してまいります。</p>
<h2>マスターコース第1期の継続について</h2>
<p>2020年2月8日に開校いたしましたマスターコース第1期につきましても、これらの感染対策を継続したうえで講義を進めてまいります。全16日間・計96時間の課程は、土日を中心に段階的に進行いたします。状況に応じ、日程の調整が必要となった場合は、受講生の皆さまへ個別にご連絡いたします。補講の体制も整えておりますので、やむを得ずご欠席となった場合もご安心ください。</p>
<h2>受講生の皆さまへのお願い</h2>
<p>ご来校の際は、手指の消毒とマスクの着用にご協力をお願いいたします。発熱や体調のすぐれない場合は、無理をなさらずご欠席いただき、後日の補講にて対応いたします。皆さまに安心して学んでいただける環境づくりに努めてまいりますので、ご理解とご協力を賜りますようお願い申し上げます。最新の対応状況は、随時このお知らせ欄にてご案内いたします。</p>`,
  },
  {
    slug: 'master-1-completion-202011',
    date: '2020年11月8日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: 'マスターコース第1期 修了のご報告',
    metaDescription:
      '2020年9月5日に開校したマスターコース第1期が、11月8日に全課程を修了しました。全16日間・計96時間で家政管理・危機対応・国際的な接遇作法までを学ぶ最上級課程。卒業ディナー形式の修了実技試験の様子を、日本の執事学校としてご報告します。',
    keywords: ['執事学校', '日本の執事学校', 'マスターコース', '修了', '執事 上級'],
    heroImage: '/images/generated/roleplay-formal-2026.jpg',
    heroAlt: '修了実技試験に臨むマスターコース第1期の受講生',
    figures: [
      {
        src: '/images/generated/advanced-service-2026.jpg',
        alt: '卒業ディナーで披露するテーブルサービスの実技',
        caption: '卒業ディナー形式の実技で給仕とテーブルサービスを披露しました',
      },
    ],
    overview: {
      heading: 'マスターコース第1期 開校概要',
      rows: [
        { label: '開校日', value: '2020年9月5日' },
        { label: '修了日', value: '2020年11月8日' },
        { label: '定員', value: '6名' },
        { label: '最少催行人数', value: '3名' },
        { label: '授業形態', value: '全16日間・計96時間／土日中心（1日6時間）' },
      ],
    },
    faq: [
      {
        question: '修了するとどのような証明が得られますか？',
        answer:
          '修了実技試験に合格された方には、修了証を授与いたします。学びの到達を示す証としてお渡ししております。',
      },
      {
        question: 'エッセンシャルコースを経ずにマスターコースを受講できますか？',
        answer:
          'マスターコースは上級課程のため、基礎を修めていることが望ましい構成です。ご経験に応じたコース選びは、個別相談にて承ります。',
      },
    ],
    related: [
      { title: 'マスターコース', desc: '最上級の総合課程', href: '/courses/master' },
      { title: '修了証について', desc: '修了認定と証の詳細', href: '/certificate' },
      { title: 'コース一覧', desc: '3コースの違いを一覧で確認', href: '/courses' },
      { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
    ],
    body: `<p>2020年9月5日に開校いたしましたマスターコース第1期が、本日2020年11月8日をもちまして全課程を修了いたしました。受講生の皆さまの真摯な学びに、心より敬意を表します。ここに、その歩みと成果をご報告いたします。</p>
<h2>マスターコース第1期の歩み</h2>
<p>マスターコースは、当アカデミーの最上級課程として、家政の管理・危機対応・国際的な接遇作法までを幅広く網羅いたします。全16日間・計96時間にわたり、土日を中心に集中的な講義と実技を重ねてまいりました。日本の執事学校として、基礎技能の先にある高度な判断力と統率力を養うことを目指した課程です。受講生の皆さまは、回を追うごとに所作へ磨きをかけ、確かな自信を身につけていかれました。</p>
<h2>修了実技試験と卒業ディナー</h2>
<p>最終日には、これまでの学びの集大成として、卒業ディナー形式の実技試験を実施いたしました。テーブルセッティングからワインサービス、給仕の流れまで、実際の場面を想定した緊張感のなかで、受講生の皆さまは日頃の成果を存分に発揮されました。所定の基準を満たされた修了生には、修了証を授与いたしました。定員6名・最少催行3名の少人数制のもと、講師が一人ひとりの成長を丁寧に見守ってまいりました。</p>
<h2>修了生の皆さまへ、そして次期に向けて</h2>
<p>修了は終わりではなく、執事としての新たな歩みの始まりです。当アカデミーは修了後も、学びを実務に活かしていただけるよう、情報提供に努めてまいります。次期マスターコースの日程は、決定次第このお知らせ欄でご案内いたします。上級の学びにご関心をお持ちの方は、資料請求（無料）より詳細をご確認ください。</p>`,
  },
  {
    slug: 'master-2026-recruitment-202606',
    date: '2026年6月5日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: '2026年度マスターコース（後期）受講生募集開始のお知らせ',
    metaDescription:
      '2026年度マスターコース（後期）の受講生募集を開始。2026年9月5日開校、全16日間・計96時間を土日中心に。家政管理・危機対応・国際的な接遇作法まで学ぶ最上級課程です。定員6名の完全少人数制。日本の執事学校としての上級課程の詳細をご案内します。',
    keywords: ['執事学校', '日本の執事学校', 'マスターコース', '執事 上級', '執事 養成'],
    heroImage: '/images/generated/master-leadership-2026.jpg',
    heroAlt: 'チームの統率と高度な接遇を学ぶマスターコースの実践演習',
    figures: [
      {
        src: '/images/generated/master-protocol-2026.jpg',
        alt: '国際的な接遇プロトコルを確認する実技の場面',
        caption: '現場で問われる判断力を実技とロールプレイで鍛えます',
      },
    ],
    overview: {
      heading: 'マスターコース（後期） 開校概要',
      rows: [
        { label: '開校日', value: '2026年9月5日' },
        { label: '申込締切', value: '2026年8月22日' },
        { label: '定員', value: '6名' },
        { label: '最少催行人数', value: '3名' },
        { label: '授業形態', value: '全16日間・計96時間／土日中心（1日6時間）' },
      ],
    },
    faq: [
      {
        question: '途中入学はできますか？',
        answer:
          '定員6名の少人数制のため、開校後の途中入学は承っておりません。お申込みは締切（2026年8月22日）までにお済ませください。',
      },
      {
        question: 'エッセンシャルコースとの違いは何ですか？',
        answer:
          'マスターコースは基礎技能に加え、家政の管理・危機対応・国際的な接遇作法までを学ぶ最上級課程です。全16日間・計96時間の構成です。',
      },
    ],
    related: [
      { title: 'マスターコース', desc: '最上級の総合課程', href: '/courses/master' },
      { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
      { title: '開校日程', desc: '各コースの最新スケジュール', href: '/schedule' },
      { title: '受講料金', desc: 'コース別の料金のご案内', href: '/pricing' },
    ],
    body: `<p>2026年度マスターコース（後期）の受講生募集を開始いたします。執事としての総合力を高めたい方に向けた、当アカデミー最上級の課程です。基礎を修めた方が、より高度な技能と品格を身につけるための集大成となる課程として設計しております。</p>
<h2>マスターコース（後期）の概要</h2>
<p>マスターコースは、基礎技能に加え、家政の管理、危機対応、国際的な接遇作法までを体系的に学ぶ課程です。全16日間・計96時間のカリキュラムを、すべて土日に開校いたします。1日6時間（90分×4コマ）の集中講義で、お仕事と両立しながら学んでいただけます。開校は2026年9月5日を予定しております。日本の執事学校として、国内外のどのような場面でも通用する応対力を養うことを目標としております。</p>
<h2>こんな方におすすめします</h2>
<p>エッセンシャルコースやアドバンスコースを修了され、さらに専門性を高めたい方、将来的にハウススチュワードや接遇責任者として活躍したい方に適した課程です。危機対応やチームの統率といった、現場で問われる実践的な判断力を、実技とロールプレイを通じて鍛えてまいります。執事学校での学びを、実務の高みへと引き上げる内容です。</p>
<h2>募集要項とお申込み</h2>
<p>本コースは完全少人数制とし、定員は6名、最少催行人数は3名といたします。お申込みの締切は2026年8月22日です。定員に達し次第、受付を締め切らせていただきますので、お早めにご検討ください。カリキュラムの詳細や受講のお手続きにつきましては、資料請求（無料）にてご案内いたします。個別のご相談も承っておりますので、お気軽にお問い合わせください。</p>`,
  },
  {
    slug: 'essential-july-2026-opening-202607',
    date: '2026年7月4日',
    category: 'schedule',
    categoryLabel: '開校情報',
    title: '2026年度エッセンシャルコース（7月期）開校のお知らせ',
    metaDescription:
      '2026年7月4日、2026年度エッセンシャルコース（7月期）を開校しました。全8日間・計48時間を土日中心に、執事の基礎の所作と接遇を集中的に学ぶ入門課程。定員6名の完全少人数制で未経験の方も安心。日本の執事学校としての学びをご案内します。',
    keywords: ['執事学校', '日本の執事学校', 'エッセンシャルコース', '執事 未経験', '執事 講座'],
    heroImage: '/images/generated/training-basic-2026.jpg',
    heroAlt: '基礎の立ち居振る舞いを学ぶエッセンシャルコース（7月期）の受講生',
    figures: [
      {
        src: '/images/generated/essential-writing-2026.jpg',
        alt: '書簡や記帳の作法を学ぶ受講生の手元のクローズアップ',
        caption: '所作の一つひとつを段階的に習得していきます',
      },
    ],
    overview: {
      heading: 'エッセンシャルコース（7月期） 開校概要',
      rows: [
        { label: '開校日', value: '2026年7月4日' },
        { label: '修了日', value: '2026年7月26日（予定）' },
        { label: '定員', value: '6名' },
        { label: '最少催行人数', value: '3名' },
        { label: '授業形態', value: '全8日間・計48時間／土日中心（1日6時間）' },
      ],
    },
    faq: [
      {
        question: '未経験でも大丈夫ですか？',
        answer:
          'はい。完全少人数制のもと、基礎の所作から段階的に学べるカリキュラムです。未経験の方も安心してお取り組みいただけます。',
      },
      {
        question: '次回の開校はいつですか？',
        answer:
          '次回以降の開校日程は、決定次第このお知らせ欄でご案内いたします。資料請求にて最新の予定をお届けします。',
      },
    ],
    related: [
      { title: 'エッセンシャルコース', desc: '基礎の所作と接遇を学ぶ入門課程', href: '/courses/essential' },
      { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
      { title: '開校日程', desc: '各コースの最新スケジュール', href: '/schedule' },
      { title: '入学案内', desc: 'お申込みの流れと条件', href: '/admission' },
    ],
    body: `<p>本日2026年7月4日、2026年度エッセンシャルコース（7月期）を開校いたしました。新たに執事への一歩を踏み出された受講生の皆さまを、心より歓迎いたします。ここに、開校のご報告と、これから執事を目指す方へのご案内を申し上げます。</p>
<h2>エッセンシャルコース（7月期）開校</h2>
<p>エッセンシャルコースは、執事に求められる基礎の所作と接遇の型を集中的に学ぶ入門課程です。全8日間・計48時間のカリキュラムを、土日を中心に進めてまいります。1日6時間（90分×4コマ）の集中講義で、平日はお仕事をされている方も無理なく学んでいただけます。修了は2026年7月26日を予定しており、修了実技試験を経て、所定の基準を満たされた方に修了証をお渡しいたします。</p>
<h2>初日の様子と学びの流れ</h2>
<p>初日は、執事という職業の歴史と心構えを学ぶ座学から始まり、立ち居振る舞いの基本へと進みました。受講生の皆さまは、姿勢や歩き方といった一見当たり前の所作にも、執事ならではの型があることを体感されていました。以降の回では、来客応対、食卓での給仕、身だしなみの整え方などを、実技を中心に段階的に習得していただきます。</p>
<h2>これから執事を目指す方へ</h2>
<p>当アカデミーでは、完全少人数制（定員6名・最少催行3名）のもとで、お一人おひとりの習得状況に合わせた丁寧な指導を行っております。基礎から着実に学べる環境を整えておりますので、未経験の方も安心してお取り組みいただけます。日本の執事学校として、確かな技能と品格を育む場でありたいと考えております。次回以降の開校日程は、決定次第このお知らせ欄でご案内いたします。受講をご検討の方は、資料請求（無料）よりお気軽にお問い合わせください。</p>`,
  },
  {
    slug: 'blog-update-202606',
    date: '2026年6月13日',
    category: 'update',
    categoryLabel: '更新情報',
    title: 'ブログ更新: 『執事とバトラーの違いとは？』ほか講師コラムを公開しました',
    body: `<p>講師によるブログコラムを公開いたしました。今回は「<a href="/blog/butler-difference">執事とバトラーの違いとは？語源と現代日本での使い分けを講師が解説</a>」をはじめ、執事のキャリアや作法に関する記事を複数掲載しております。執事という職業への理解を深めていただける内容です。ぜひご一読ください。</p>`,
  },
  {
    slug: 'blog-update-202607',
    date: '2026年7月4日',
    category: 'update',
    categoryLabel: '更新情報',
    title: 'ブログ更新: 『タイの大学が執事養成へ？海外バトラー教育の最新動向』を公開しました',
    body: `<p>講師によるブログコラムを公開いたしました。今回は海外の執事教育の動向を取り上げた「<a href="/blog/global-butler-news-202607-5ce73425">タイの大学が執事養成へ？海外バトラー教育の最新動向を専門講師が解説</a>」を掲載しております。世界の潮流と、日本の執事教育の特色を知る手がかりとなる内容です。ぜひご覧ください。</p>`,
  },
];

const autoArticles: NewsArticle[] = (newsAutoData as unknown as NewsArticle[]).map((a) => ({
  slug: a.slug,
  date: a.date,
  category: a.category,
  categoryLabel: a.categoryLabel,
  title: a.title,
  body: a.body,
}));

// "YYYY年M月D日"(月日はゼロ埋めなし)を YYYYMMDD の数値に正規化する。
// localeCompare では "2020年2月28日" > "2020年11月8日" のように桁数混在で降順が壊れるため。
function jpDateValue(date: string): number {
  const m = date.match(/(\d+)年(\d+)月(\d+)日/);
  if (!m) return 0;
  return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
}

export const newsArticles: NewsArticle[] = [
  ...manualArticles,
  ...newsArchive2021,
  ...newsArchive2022,
  ...newsArchive2023,
  ...newsArchive2024,
  ...newsArchive2025,
  ...autoArticles,
].sort((a, b) => jpDateValue(b.date) - jpDateValue(a.date));

export const newsFaqs = [
  {
    question: '開校日程はどこで分かりますか?',
    answer: '決定し次第このお知らせ欄でご案内します。資料請求でも最新の予定をお届けします。',
  },
  {
    question: '説明会はありますか?',
    answer: '開催が決まり次第こちらでご案内します。1日完結の体験講座もご用意しています。',
  },
  {
    question: '更新はどのくらいの頻度ですか?',
    answer: '準備の進捗に合わせて随時更新します。',
  },
];

export const newsRelatedPages = [
  {
    title: '資料請求',
    description: '無料の資料をお届け',
    href: '/brochure',
  },
  {
    title: 'コース一覧・比較',
    description: '3コースの違いを一覧で確認',
    href: '/courses',
  },
  {
    title: 'サイトマップ',
    description: 'すべてのページを一覧で確認',
    href: '/sitemap',
  },
  {
    title: 'アカデミー概要',
    description: '日本執事アカデミーについて',
    href: '/about',
  },
];
